import { formatMonthName, formatSEK } from '@budgie/shared';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { type RouterOutputs, trpc } from '../../lib/trpc';
import { cn } from '../../lib/utils';
import { EmptyState } from '../common/EmptyState';
import { TableSkeleton } from '../common/Skeleton';

type Props = {
  loanId: string;
};

type HistoryTransactions = RouterOutputs['loan']['history'][number]['transactions'];

function TransactionDetails({ transactions }: { transactions: HistoryTransactions }) {
  if (transactions.length === 0) {
    return <span className="text-sm text-muted-foreground/70">Ingen betalning</span>;
  }

  return (
    <details>
      <summary className="flex min-h-11 cursor-pointer items-center text-sm font-medium text-primary md:min-h-0">
        Visa transaktioner
      </summary>
      <div className="mt-2 space-y-2 rounded-lg border border-border bg-muted/20 p-3">
        {transactions.map((t) => (
          <div key={t.id} className="flex items-center justify-between gap-3 text-sm">
            <div className="min-w-0">
              <div className="break-words font-medium text-foreground">{t.description}</div>
              <div className="text-xs text-muted-foreground">
                {t.date}
                {t.accountName ? ` · ${t.accountName}` : ''}
              </div>
            </div>
            <div className="shrink-0 tabular-nums text-foreground">{formatSEK(t.amount)}</div>
          </div>
        ))}
      </div>
    </details>
  );
}

export function LoanHistory({ loanId }: Props) {
  const query = trpc.loan.history.useQuery({ loanId, months: 12 }, { retry: false });

  if (query.isLoading) {
    return <TableSkeleton rows={6} columns={4} />;
  }

  if (query.error) {
    return (
      <EmptyState
        icon="⚠️"
        title="Kunde inte hämta historik"
        description="Försök igen om en liten stund."
      />
    );
  }

  const rows = query.data ?? [];
  const hasPayments = rows.some((row) => row.transactions.length > 0);

  if (!hasPayments) {
    return (
      <EmptyState
        icon="🗓️"
        title="Ingen betalningshistorik"
        description="När lånet har matchade transaktioner visas historiken här."
      />
    );
  }

  return (
    <Card size="sm">
      <CardHeader>
        <CardTitle className="text-sm font-semibold">Historik</CardTitle>
      </CardHeader>
      <CardContent>
        {/* Phones: a plain list inside the card (no extra box) with one line
            per empty month instead of three. */}
        <ul className="divide-y divide-border md:hidden">
          {rows.map((row) => {
            const count = row.transactions.length;
            return (
              <li key={`${row.year}-${row.month}`} className="py-2.5 first:pt-0 last:pb-0">
                <div className="flex items-baseline justify-between gap-3">
                  <span className="font-medium capitalize text-foreground">
                    {formatMonthName(row.month)} {row.year}
                  </span>
                  <span
                    className={cn(
                      'whitespace-nowrap tabular-nums',
                      count === 0 ? 'text-muted-foreground' : 'text-foreground',
                    )}
                  >
                    {formatSEK(row.totalAmount)}
                  </span>
                </div>
                <div className="text-xs text-muted-foreground">
                  {count === 0
                    ? 'Ingen betalning'
                    : `${count} ${count === 1 ? 'betalning' : 'betalningar'}`}
                </div>
                {count > 0 && <TransactionDetails transactions={row.transactions} />}
              </li>
            );
          })}
        </ul>

        <div className="hidden overflow-x-auto rounded-lg border border-border md:block">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Månad</TableHead>
                <TableHead className="text-right">Belopp</TableHead>
                <TableHead className="text-right">Betalningar</TableHead>
                <TableHead>Detaljer</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {rows.map((row) => (
                <TableRow key={`${row.year}-${row.month}`}>
                  <TableCell className="capitalize">
                    {formatMonthName(row.month)} {row.year}
                  </TableCell>
                  <TableCell className="text-right tabular-nums">
                    {formatSEK(row.totalAmount)}
                  </TableCell>
                  <TableCell className="text-right text-muted-foreground">
                    {row.transactions.length}
                  </TableCell>
                  <TableCell>
                    <TransactionDetails transactions={row.transactions} />
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </CardContent>
    </Card>
  );
}
